// AyudaPage — chat contra el bot de ayuda (POST /help/ask del núcleo).
// v1.0 (2026-08-31): mismo patrón que la página Ayuda de la agenda dental y el
// HelpBotCard del manager lealtad; historial solo en memoria, mini-parser de
// **negritas**/listas (sin librería de markdown). Tokens de marca Let's Move.
// v1.1 (2026-09-30, Karla): la conversación se GUARDA en el navegador, una por usuario
// (las cajeras comparten compu). Antes vivía solo en memoria y al irse a Caja/Clases a
// aplicar el paso se borraba. Una respuesta que llega con la página cerrada también se guarda.
// v1.2 (2026-09-30, Karla: «que se replique en el cel»): la fuente es el SERVIDOR
// (GET /help/history = mis preguntas desde mi última «Nueva conversación»). El navegador queda
// como copia rápida: pinta al instante y cubre la pregunta en curso o un fallo de red.
import { FormEvent, Fragment, ReactNode, useEffect, useRef, useState } from 'react';
import { HelpCircle, Send, Loader2, MessageCircleWarning, RotateCcw } from 'lucide-react';
import { askHelpBot, getHelpHistory, clearHelpHistory, HelpHistoryRow } from '../../services/helpApi';
import { ApiError } from '../../services/apiClient';
import { useAuth } from '../../context/AuthContext';

interface ChatMessage {
  id: number;
  role: 'user' | 'assistant' | 'error';
  text: string;
}

const SUGGESTED_QUESTIONS = [
  '¿Cómo clono las clases al nuevo ciclo?',
  '¿Por qué una alumna sale en amarillo?',
  '¿Cómo registro un pago de curso completo?',
  '¿Cómo abro la caja del día?',
];

const FALLBACK_ERROR_BY_STATUS: Record<number, string> = {
  400: 'Tu pregunta es muy corta — cuéntame un poco más de lo que necesitas.',
  429: 'Se alcanzaron muchas preguntas seguidas. Espera un momento y vuelve a intentar.',
  502: 'El asistente no pudo responder en este momento. Intenta de nuevo en unos segundos.',
};

let nextId = Date.now();   // ids únicos también frente a los guardados de otra visita

const MAX_GUARDADOS = 60;     // ~30 preguntas con su respuesta
const EVT = 'lm-ayuda-cambio';
const storeKey = (userId: number | undefined) => `lm-ayuda-${userId ?? 'anon'}`;

function leer(key: string): ChatMessage[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(v) ? v : [];
  } catch { return []; }
}
function guardar(key: string, lista: ChatMessage[]) {
  try { localStorage.setItem(key, JSON.stringify(lista.slice(-MAX_GUARDADOS))); } catch { /* sin espacio / privado */ }
  window.dispatchEvent(new CustomEvent(EVT, { detail: { key, lista } }));
}
/** Filas del servidor → burbujas. Una pregunta que falló (ok=0) sale como aviso. */
function desdeServidor(rows: HelpHistoryRow[]): ChatMessage[] {
  const out: ChatMessage[] = [];
  for (const r of rows) {
    out.push({ id: r.log_id * 2, role: 'user', text: r.question });
    out.push(r.ok
      ? { id: r.log_id * 2 + 1, role: 'assistant', text: r.answer || 'No tengo una respuesta para eso.' }
      : { id: r.log_id * 2 + 1, role: 'error', text: 'El asistente no pudo responder esta vez. Pregunta de nuevo si aún la necesitas.' });
  }
  return out;
}

/** Añade al historial GUARDADO (no al estado): así la respuesta que llega después de
    salir de la página no se pierde. Avisa a la página si está abierta. */
function agregar(key: string, msg: ChatMessage) {
  guardar(key, [...leer(key), msg]);
}

/** Negritas **texto** dentro de una línea, sin librería de markdown. */
function renderInlineBold(line: string, keyPrefix: string): ReactNode[] {
  const parts = line.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
      return <strong key={`${keyPrefix}-b-${i}`}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={`${keyPrefix}-t-${i}`}>{part}</Fragment>;
  });
}

/** Texto completo -> líneas, con sangría para "1. …" / "- …" / "• …". */
function renderAnswer(text: string): ReactNode {
  const lines = text.split('\n');
  return lines.map((line, idx) => {
    const trimmed = line.trim();
    if (trimmed === '') return <div key={idx} className="h-2" />;
    const isListItem = /^(\d+[.)]|[-*•])\s+/.test(trimmed);
    return (
      <p key={idx} className={`leading-relaxed ${isListItem ? 'pl-3' : ''} ${idx > 0 ? 'mt-1' : ''}`}>
        {renderInlineBold(line, String(idx))}
      </p>
    );
  });
}

export default function AyudaPage() {
  const { user } = useAuth();
  const key = storeKey(user?.account_id);
  const [messages, setMessages] = useState<ChatMessage[]>(() => leer(key));
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  useEffect(() => {
    setMessages(leer(key));
    const onCambio = (e: Event) => {
      const d = (e as CustomEvent<{ key: string; lista: ChatMessage[] }>).detail;
      if (d?.key === key) setMessages(d.lista);
    };
    window.addEventListener(EVT, onCambio);
    // El servidor manda (mismo historial en compu y cel). Si no responde, queda la copia local.
    let vivo = true;
    getHelpHistory()
      .then((rows) => { if (vivo) guardar(key, desdeServidor(rows)); })
      .catch(() => { /* sin red: se queda lo local */ });
    return () => { vivo = false; window.removeEventListener(EVT, onCambio); };
  }, [key]);

  const nuevaConversacion = async () => {
    if (!window.confirm('¿Borrar esta conversación y empezar otra?')) return;
    try { await clearHelpHistory(); } catch {
      window.alert('No se pudo borrar la conversación (¿sin internet?). Intenta de nuevo.');
      return;   // si el servidor no la ocultó, volvería a salir en el cel: mejor no fingir
    }
    try { localStorage.removeItem(key); } catch { /* no-op */ }
    setMessages([]);
  };

  const send = async (question: string) => {
    const text = question.trim();
    if (!text || sending) return;

    agregar(key, { id: nextId++, role: 'user', text });
    setInput('');
    setSending(true);

    try {
      const answer = await askHelpBot(text);
      agregar(key, { id: nextId++, role: 'assistant', text: answer || 'No tengo una respuesta para eso.' });
    } catch (err) {
      const e = err as ApiError;
      const isGenericFallback = !e.message || /^HTTP \d+$/.test(e.message);
      const friendly = (isGenericFallback && e.status && FALLBACK_ERROR_BY_STATUS[e.status])
        || e.message
        || 'No se pudo obtener respuesta. Intenta de nuevo en un momento.';
      agregar(key, { id: nextId++, role: 'error', text: friendly });
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    send(input);
  };

  return (
    <div className="flex h-full w-full flex-col">
      <div className="mb-4">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content">
          <HelpCircle className="text-brand-blue" size={24} /> Ayuda
        </h2>
        <div className="mt-1 flex items-start justify-between gap-3">
          <p className="text-sm text-content-3">
            Pregúntame cómo usar el sistema: alumnas, clases, cobros, adeudos, caja…
            {messages.length > 0 && <span className="block text-xs">Tu conversación se queda guardada (también en tu cel): puedes ir a aplicar los pasos y regresar.</span>}
          </p>
          {messages.length > 0 && (
            <button type="button" onClick={nuevaConversacion} disabled={sending}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-line px-2.5 py-1.5 text-xs text-content-2 transition hover:bg-surface-2 disabled:opacity-40"
              data-testid="ayuda-nueva">
              <RotateCcw size={13} /> Nueva conversación
            </button>
          )}
        </div>
      </div>

      {/* Preguntas de ejemplo — siempre a la mano */}
      <div className="mb-3 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {SUGGESTED_QUESTIONS.map((q) => (
          <button
            key={q}
            type="button"
            disabled={sending}
            onClick={() => send(q)}
            className="shrink-0 whitespace-nowrap rounded-full border border-brand-blue/30 bg-brand-blue/10 px-3 py-1.5 text-xs font-medium text-brand-blue transition hover:bg-brand-blue/20 disabled:opacity-40"
          >
            {q}
          </button>
        ))}
      </div>

      {/* Tarjeta del chat */}
      <div className="flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3 sm:p-4">
          {messages.length === 0 && (
            <div className="flex h-full flex-col items-center justify-center text-center text-content-3">
              <HelpCircle size={32} className="mb-2 opacity-50" />
              <p className="text-sm">Escribe tu duda o toca una de las preguntas de arriba.</p>
            </div>
          )}

          {messages.map((m) => {
            if (m.role === 'user') {
              return (
                <div key={m.id} className="flex justify-end">
                  <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-brand-blue px-3.5 py-2 text-sm text-white shadow-sm">
                    {m.text}
                  </div>
                </div>
              );
            }
            if (m.role === 'error') {
              return (
                <div key={m.id} className="flex justify-start">
                  <div className="flex max-w-[85%] items-start gap-2 rounded-2xl rounded-bl-sm bg-amber-500/10 px-3.5 py-2 text-sm text-amber-600 shadow-sm dark:text-amber-300">
                    <MessageCircleWarning size={16} className="mt-0.5 shrink-0" />
                    <span>{m.text}</span>
                  </div>
                </div>
              );
            }
            return (
              <div key={m.id} className="flex justify-start">
                <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-canvas px-3.5 py-2 text-sm text-content shadow-sm">
                  {renderAnswer(m.text)}
                </div>
              </div>
            );
          })}

          {sending && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-canvas px-3.5 py-2 text-sm text-content-3 shadow-sm">
                <Loader2 size={14} className="animate-spin" /> Pensando…
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="flex items-center gap-2 border-t border-line p-2.5">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Escribe tu pregunta…"
            disabled={sending}
            className="flex-1 rounded-full border border-line bg-canvas px-4 py-2.5 text-sm text-content placeholder:text-content-3 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-blue text-white transition hover:opacity-90 disabled:opacity-40"
            title="Enviar"
            aria-label="Enviar pregunta"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      </div>

      <p className="mt-3 text-center text-[11px] text-content-3">
        Si el asistente no resuelve tu duda, escríbenos por WhatsApp con: alumna o clase, fecha, y captura.
      </p>
    </div>
  );
}
